import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components';
import { DropdownV2, Input } from 'components/form';
import { Row } from 'components/layout';
import MultiSelect from 'components/MultiSelect';
import { ButtonV3, TabRadioGroupV2 } from 'components/ComponentV2';
import { confirmationModalInstance } from 'components/ComponentV2/ConfirmationModal';
import Header from 'components/Header';
import { ConfirmationButtonResponse } from 'hooks/UseCompanyCampaigns';
import { useNotification } from 'hooks/useNotification';
import { TouchpointType } from 'lib/models/touchpoint';
import { Option } from 'lib/models/option';
import { JobAttributes, JobRequestStep1, WorkingModel } from 'lib/models/job';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';

import { useCampaigns } from 'lib/contexts/campaigns-context';
import { TouchpointState } from 'lib/models/opportunity';
import styles from '../Step.module.scss';

const OpportunityTypeOption = [
  {
    id: '1',
    value: TouchpointType.Job,
    label: TouchpointType.Job,
  },
  {
    id: '2',
    value: TouchpointType.Internship,
    label: TouchpointType.Internship,
  },
];

export interface Step1Props {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  touchpoint: JobAttributes | null;
  areaOfResponsibilities: Array<Option>;
  internshipKindOptions: Array<Option>;
  cityCountryList: Array<Option & { country_name: string; country_id: string }>;
  onSave: (
    data: JobRequestStep1,
    isValueEdited: boolean,
    goToStep: number
  ) => void;
  isLoading: boolean;
}

export const Step1: React.FC<Step1Props> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    touchpoint,
    areaOfResponsibilities,
    internshipKindOptions,
    cityCountryList,
    onSave,
    isLoading,
  }: Step1Props) => {
    const contentClassNames = cn(
      {
        [styles.Step]: true,
      },
      className
    );
    const router = useRouter();
    const [t] = useTranslation('opportunity.posting');

    const { type, onboarding } = router.query;
    const notificationInstance = useNotification();
    const { mutateCompanyCampaigns } = useCampaigns();

    const [title, setTitle] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<Array<string>>([]);
    const [opportunityType, setOpportunityType] = useState<TouchpointType>(
      TouchpointType.Job
    );
    const [internshipType, setInternshipType] = useState<string>('');
    const [cities, setCities] = useState<Array<string>>([]);
    const [touchpointableId, setTouchpointableId] = useState<string | null>(
      null
    );
    const [workMode, setWorkMode] = useState<WorkingModel>(WorkingModel.OFFICE);
    const [availableTouchpointData, setAvailableTouchpointData] =
      useState<JobRequestStep1>();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const titleContainerRef = useRef<HTMLDivElement | null>(null);
    const roleContainerRef = useRef<HTMLDivElement | null>(null);
    const locationContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (type) {
        setOpportunityType(String(type) as TouchpointType);
      }
    }, [type]);

    useEffect(() => {
      if (touchpoint) {
        const {
          title,
          kind,
          touchpointable_type,
          area_of_responsibilities: aorResponse,
          cities: citiesResponse,
        } = touchpoint;
        const { id: touchpointableIdResponse, working_model } = parseResponse(
          touchpoint.touchpointable
        );
        const cities = parseArrayResponse(citiesResponse);
        const citiesSelected = cities.map((city) => city.name);

        const aor = parseArrayResponse(aorResponse);
        const aorSelected = aor.map((item) => item.id);

        setAvailableTouchpointData({
          title,
          touchpointable_attributes: {
            id: touchpointableIdResponse,
            type: touchpointable_type,
            kind,
            working_model,
          },
          area_of_responsibility_ids: aorSelected,
          city_ids: citiesSelected,
          is_online: working_model === WorkingModel.REMOTE ? true : false,
        });
        setTitle(title);
        setOpportunityType(touchpointable_type);
        setInternshipType(kind);
        setTouchpointableId(touchpointableIdResponse);

        setCities(citiesSelected);
        setSelectedRoles(aorSelected);

        setWorkMode(working_model);
      }
    }, [touchpoint]);

    const areValuesEdited = () => {
      if (touchpoint) {
        const selectedAttributes = {
          title,
          touchpointable_attributes: {
            id: touchpointableId,
            type: opportunityType,
            kind:
              opportunityType === TouchpointType.Internship
                ? internshipType
                : 'Entry Level',
            working_model: workMode,
          },
          area_of_responsibility_ids: selectedRoles,
          city_ids: cities,
          is_online: workMode === WorkingModel.REMOTE ? true : false,
        };

        return !isEqual(selectedAttributes, availableTouchpointData);
      } else {
        return (
          !!title ||
          selectedRoles.length > 0 ||
          (opportunityType === TouchpointType.Internship && !!internshipType) ||
          !!workMode ||
          ((workMode === WorkingModel.OFFICE ||
            workMode === WorkingModel.HYBRID) &&
            cities.length > 0)
        );
      }
    };

    const onClose = async () => {
      if (areValuesEdited()) {
        const flag = await confirmationModalInstance();
        if (flag === ConfirmationButtonResponse.SAVE) {
          onSubmit({ checkRequired: true, submitType: TouchpointState.SAVE });
        } else if (flag === ConfirmationButtonResponse.DONT_SAVE) {
          if (onboarding) {
            router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
            return;
          }
          history.back();
        }
      } else {
        if (onboarding) {
          router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
          return;
        }
        mutateCompanyCampaigns();
        history.back();
      }
    };

    const onNavigate = (goToStep: number) => {
      if (goToStep === step) return;

      if (touchpoint) {
        const { responsibilities, requirements } = touchpoint;
        const { full_time } = parseResponse(touchpoint.touchpointable);
        if (
          (full_time === null ||
            responsibilities === null ||
            requirements === null) &&
          goToStep === 3
        ) {
          notificationInstance.error({
            title: 'Details section not filled.',
            message: `Fill the Details section to move to Application info`,
          });
          return;
        }
      }

      onSubmit({ checkRequired: true, goToStep });
    };
    const [shouldScroll, setShouldScroll] = useState(false);

    useEffect(() => {
      if (shouldScroll) {
        if (!title) {
          titleContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        if (selectedRoles.length === 0) {
          roleContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        if (cities.length === 0) {
          locationContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        setShouldScroll(false);
      }
    }, [shouldScroll, title, selectedRoles, cities]);

    const selectedRolesText = useMemo(() => {
      const labelsList = areaOfResponsibilities
        ?.filter((item) => {
          if (selectedRoles.includes(item.id)) return item.label;
        })
        .map((item) => item.label);
      return labelsList?.join(', ') || 'Select a role';
    }, [selectedRoles]);

    const citiesDescText =
      workMode !== WorkingModel.REMOTE
        ? 'Select a city'
        : 'Select preferred cities';
    const selectedCitiesText = cities?.join(', ') || citiesDescText;

    const onSubmit = ({
      checkRequired,
      submitType,
      goToStep = 2,
    }: {
      checkRequired: boolean;
      submitType?: TouchpointState;
      goToStep?: number;
    }) => {
      if (!title || selectedRoles.length === 0 || cities.length === 0) {
        setShouldScroll(true);
      }
      const onSaveRequest = () => {
        const isValueEdited = areValuesEdited() as boolean;
        const citiesId = cityCountryList
          .filter((item) => {
            if (cities.includes(item.label)) return item.id;
          })
          .map((item) => item.id);
        const data = {
          title,
          touchpointable_attributes: {
            id: touchpointableId || '',
            type: opportunityType,
            working_model: workMode,
            kind:
              opportunityType === TouchpointType.Internship
                ? internshipType
                : undefined,
          },
          area_of_responsibility_ids: selectedRoles,
          city_ids: citiesId,
          is_online: workMode === WorkingModel.REMOTE ? true : false,
          submitType,
        };
        setShouldScroll(false);
        onSave(data, isValueEdited, goToStep);
      };

      if (checkRequired) {
        setIsSubmitted(true);
        if (isInvalid) return;
      }
      onSaveRequest();
    };

    const isInvalid =
      !title ||
      selectedRoles.length === 0 ||
      (opportunityType === TouchpointType.Internship && !internshipType) ||
      !workMode ||
      ((workMode === WorkingModel.OFFICE || workMode === WorkingModel.HYBRID) &&
        cities.length === 0);

    const workingModelOption = Object.keys(WorkingModel).map((key) => ({
      id: key,
      value: WorkingModel[key as keyof typeof WorkingModel],
      label: WorkingModel[key as keyof typeof WorkingModel],
    }));

    useEffect(() => {
      if (opportunityType === TouchpointType.Internship) {
        setInternshipType('Off-cycle Internship');
      }
    }, [opportunityType]);

    const UKCitiesList = useMemo(() => {
      return cityCountryList.filter(
        (item) => item.country_name === 'United Kingdom'
      );
    }, [cityCountryList]);

    return (
      <>
        <Header
          step={step as number}
          onClose={onClose}
          onNavigate={(goToStep: number) => onNavigate(goToStep)}
          onNext={() => onNavigate(2)}
          onNextLoading={isLoading}
          className={styles.header}
        />
        <div className={contentClassNames} style={style} data-testid="Step1">
          <div className={styles.container}>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title} ref={titleContainerRef}>
                  {t('job-posting.basic-info')}
                </p>
                <p className={styles.desc}>
                  {t('job-posting.basic-info-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <div className={styles.inputContainer}>
                  <Input
                    label={t('job-posting.title')}
                    placeholder={t('job-posting.software-engineer')}
                    maxLength={120}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    isValid={!(isSubmitted && !title)}
                    className={cn(styles.input)}
                  />
                  {isSubmitted && !title ? (
                    <p className={styles.required}>
                      {' '}
                      {t('job-posting.required')}
                    </p>
                  ) : null}
                </div>
                <div className={styles.gap}></div>
                <TabRadioGroupV2
                  title={t('job-posting.opportunity-type')}
                  options={OpportunityTypeOption}
                  value={opportunityType}
                  onChange={(value: string) => {
                    setOpportunityType(value as TouchpointType);
                  }}
                  //todo disable this when state is submit for approval
                />
                {opportunityType === TouchpointType.Internship ? (
                  <>
                    <div className={styles.gap}></div>
                    <div className={styles.inputContainer}>
                      <div className={styles.StepLabel}>
                        {t('job-posting.internship-type')}
                      </div>
                      {isSubmitted && !internshipType ? (
                        <p className={styles.required}>
                          {' '}
                          {t('job-posting.required')}
                        </p>
                      ) : null}
                      <DropdownV2
                        label={t('job-posting.select-internship-type')}
                        title=""
                        value={internshipType}
                        onChange={(value) => setInternshipType(value)}
                        options={internshipKindOptions}
                        selectedValueLabel={internshipType}
                        isValid={!(isSubmitted && !internshipType)}
                        className={cn(styles.dropdown, {
                          [styles.dropdownInvalid]:
                            isSubmitted && !internshipType,
                        })}
                      />
                    </div>
                  </>
                ) : null}
                <div className={styles.gap}></div>
                <div className={styles.inputContainer} ref={roleContainerRef}>
                  <div className={styles.StepLabel}>
                    {' '}
                    {t('job-posting.role')}
                  </div>
                  {isSubmitted && selectedRoles.length === 0 ? (
                    <p className={styles.required}>
                      {' '}
                      {t('job-posting.required')}
                    </p>
                  ) : null}
                  <MultiSelect
                    title={
                      selectedRoles.length === 0
                        ? 'Select a role'
                        : selectedRolesText
                    }
                    placeholder="Search"
                    options={areaOfResponsibilities as Array<Option>}
                    values={selectedRoles}
                    onChange={(id) => setSelectedRoles(id)}
                    className={cn(styles.dropdown, {
                      [styles.dropdownInvalid]:
                        isSubmitted && selectedRoles.length === 0,
                    })}
                    dropdownClassName={styles.dropdownSelect}
                    showCount={false}
                    disableOptions={selectedRoles?.length >= 2}
                    isSearchable
                  />
                  <Row align="center" className={styles.infoRow}>
                    <Icon iconName="information" size="xsmall" />
                    <p className={styles.info}>{t('job-posting.role-info')}</p>
                  </Row>
                </div>
              </main>
            </section>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>
                  {t('job-posting.location-work-mode')}
                </p>
                <p className={styles.desc}>
                  {t('job-posting.location-work-mode-info')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <div className={styles.inputContainer}>
                  <div className={styles.StepLabel}>
                    {' '}
                    {t('job-posting.work-mode')}
                  </div>
                  {isSubmitted && !workMode ? (
                    <p className={styles.required}>
                      {' '}
                      {t('job-posting.required')}
                    </p>
                  ) : null}
                  <DropdownV2
                    label={t('job-posting.select-work-mode')}
                    title=""
                    value={workMode}
                    onChange={(value) => {
                      setWorkMode(value as WorkingModel);
                      setCities([]);
                    }}
                    options={workingModelOption}
                    selectedValueLabel={workMode}
                    isValid={!(isSubmitted && !workMode)}
                    className={cn(styles.dropdown, {
                      [styles.dropdownInvalid]: isSubmitted && !workMode,
                    })}
                  />
                </div>
                {workMode === WorkingModel.REMOTE ? (
                  <>
                    <div className={styles.gap}></div>
                    <div className={styles.inputContainer}>
                      <div className={styles.StepLabel}>
                        {t('job-posting.fully-remote-in')}{' '}
                        <span>( {t('job-posting.optional')})</span>
                      </div>

                      <MultiSelect
                        title={
                          cities.length === 0
                            ? citiesDescText
                            : selectedCitiesText
                        }
                        placeholder="Search"
                        options={UKCitiesList}
                        values={cities}
                        onChange={(id) => setCities(id)}
                        className={cn(styles.dropdown)}
                        dropdownClassName={styles.dropdownSelect}
                        showCount={false}
                        isSearchable
                        position="top"
                      />
                    </div>
                    <Row align="start" wrap={false} className={styles.infoRow}>
                      <Icon iconName="information" size="small" />
                      <p className={styles.info}>
                        {t('job-posting.select-city-info')}
                      </p>
                    </Row>
                  </>
                ) : (
                  <>
                    <div className={styles.gap}></div>
                    <div className={styles.inputContainer}>
                      <div
                        className={styles.StepLabel}
                        ref={locationContainerRef}
                      >
                        {t('job-posting.location')}
                      </div>
                      {isSubmitted && cities.length === 0 ? (
                        <p className={styles.required}>
                          {' '}
                          {t('job-posting.required')}
                        </p>
                      ) : null}
                      <MultiSelect
                        title={
                          cities.length === 0
                            ? citiesDescText
                            : selectedCitiesText
                        }
                        placeholder={t('job-posting.search')}
                        options={UKCitiesList}
                        values={cities}
                        onChange={(id) => setCities(id)}
                        className={cn(styles.dropdown, {
                          [styles.dropdownInvalid]:
                            isSubmitted && cities.length === 0,
                        })}
                        dropdownClassName={styles.dropdownSelect}
                        showCount={false}
                        disableOptions={
                          workMode === WorkingModel.OFFICE &&
                          cities?.length === 1
                        }
                        isSearchable
                        position="top"
                      />
                    </div>
                  </>
                )}
              </main>
            </section>
            <ButtonV3
              type="submit"
              onClick={() => onSubmit({ checkRequired: true })}
              className={styles.next}
              isLoading={isLoading}
              disabled={isSubmitted && isInvalid}
            >
              {t('job-posting.next')}
            </ButtonV3>
          </div>
        </div>
      </>
    );
  }
);

Step1.displayName = 'Step1';
